import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowRight, ShieldCheck } from "lucide-react";
import AuthShell from "@/components/marketplace/AuthShell";
import { useAuth } from "@/contexts/AuthContext";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startGoogleAuth = () => {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const nextParam = new URLSearchParams(location.search).get("next");
  const from = nextParam || location.state?.from || "/";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [showPass, setShowPass] = useState(false);
  const [twoFactor, setTwoFactor] = useState("");
  const [need2fa, setNeed2fa] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password, remember, need2fa ? twoFactor : undefined);
      toast.success("Welcome back to Gulf Coast Work.");
      navigate(from, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.error || "Invalid credentials";
      if (err?.response?.headers?.["x-2fa-required"] === "1") {
        setNeed2fa(true);
        toast.info("Enter your 2FA code to continue.");
      } else {
        toast.error(detail);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Welcome back." subtitle="Sign in to book pros, hire freelancers, or manage your provider profile.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <button
          type="button"
          onClick={startGoogleAuth}
          data-testid="login-google-button"
          className="w-full inline-flex items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 font-medium text-[#071b22] hover:border-[#165a71]/40 hover:bg-slate-50 transition-colors"
        >
          <GoogleIcon /> Continue with Google
        </button>
        <div className="flex items-center gap-3 my-6">
          <div className="h-px flex-1 bg-slate-200" />
          <span className="text-xs text-[#252A34]/60 uppercase tracking-wider">or</span>
          <div className="h-px flex-1 bg-slate-200" />
        </div>

        <Field label="Email">
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            data-testid="login-email"
            className="input-primary"
          />
        </Field>
        <Field label="Password">
          <div className="relative">
            <input
              type={showPass ? "text" : "password"}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              data-testid="login-password"
              className="input-primary pr-11"
            />
            <button type="button" onClick={() => setShowPass((v) => !v)} aria-label="Toggle password" className="absolute right-3 top-1/2 -translate-y-1/2 text-[#252A34]/50 hover:text-[#165a71]">
              {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </Field>

        {need2fa && (
          <Field label="2FA code">
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={twoFactor}
              onChange={(e) => setTwoFactor(e.target.value.replace(/\D/g, ""))}
              placeholder="6-digit code"
              data-testid="login-2fa"
              className="input-primary"
            />
          </Field>
        )}

        <div className="flex items-center justify-between text-sm">
          <label className="inline-flex items-center gap-2 text-[#252A34]/70">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              data-testid="login-remember"
              className="h-4 w-4 rounded border-slate-300 text-[#165a71] focus:ring-[#165a71]"
            />
            Remember me
          </label>
          <Link to="/forgot-password" className="font-medium text-[#165a71] hover:text-[#11485a]" data-testid="login-forgot-link">
            Forgot password?
          </Link>
        </div>

        <button
          type="submit"
          disabled={loading}
          data-testid="login-submit"
          className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#165a71] text-white px-6 py-3 font-medium hover:bg-[#11485a] disabled:opacity-60 transition-colors"
        >
          {loading ? "Signing in…" : (<>Sign in <ArrowRight className="h-4 w-4" /></>)}
        </button>
        <p className="text-sm text-center text-[#252A34]/70 pt-2">
          New here?{" "}
          <Link to="/register" className="font-medium text-[#165a71]" data-testid="login-to-register-link">
            Create an account
          </Link>
        </p>
        <p className="mt-6 text-xs text-[#252A34]/50 inline-flex items-center gap-2 justify-center w-full">
          <ShieldCheck className="h-3.5 w-3.5" /> We never share your data with third parties.
        </p>
      </form>
      <style>{FIELD_STYLES}</style>
    </AuthShell>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-sm font-medium text-[#071b22]">{label}</span>
    {children}
  </label>
);

export const FIELD_STYLES = `
.input-primary { width: 100%; border-radius: 0.75rem; border: 1px solid #E2E8F0; background: #fff; padding: 0.75rem 1rem; font-size: 0.95rem; color: #071b22; outline: none; transition: border-color .15s, box-shadow .15s; }
.input-primary:focus { border-color: #165a71; box-shadow: 0 0 0 3px rgba(22,90,113,0.15); }
`;

export const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden>
    <path fill="#EA4335" d="M24 9.5c3.9 0 7.4 1.4 10.1 3.7l7.5-7.5C36.9 2.1 30.9 0 24 0 14.6 0 6.4 5.4 2.5 13.2l8.7 6.8C13.1 14.3 18.1 9.5 24 9.5z"/>
    <path fill="#34A853" d="M46.1 24.5c0-1.6-.1-3.1-.4-4.6H24v9.1h12.4c-.5 2.7-2.1 5-4.4 6.6l7 5.4c4.1-3.8 6.5-9.4 6.5-16z"/>
    <path fill="#4A90E2" d="M11.2 28.7c-.6-1.8-1-3.7-1-5.7s.4-3.9 1-5.7l-8.7-6.8C.9 14.9 0 19.3 0 24s.9 9.1 2.5 13.5l8.7-6.8z"/>
    <path fill="#FBBC05" d="M24 48c6.5 0 11.9-2.1 15.9-5.8l-7-5.4c-2 1.4-4.6 2.2-8.9 2.2-5.9 0-10.9-4-12.7-9.5l-8.7 6.8C6.4 42.6 14.6 48 24 48z"/>
  </svg>
);
